import { useEffect, useState, useCallback, useRef } from "react"

const MESSAGES = [
  "Drifting through late-night frequencies",
  "Following a strange thread between genres",
  "Pulling records from the hidden shelf",
  "Crossfading constellations of sound",
  "Listening for the part that feels like you",
  "Dusting off a forgotten B-side",
  "Tracing signal through the fog",
  "Asking the basement oracle (politely)",
  "Mapping vibes to coordinates",
  "Checking one more crate... just one",
]

const ASCII_ART = `
      .  *     .        .    *
   *    .  ___---___   .   *
      .  /  (|||)  \\\\     .
   .     \\__  ^  __/   *     .
       *   /_____\\   .    *
    .   *  | .  . |     .
         . |  <>  |  *
    *      |_____|     .
         .   *     .       *
`

interface WanderingModalProps {
  allowDismiss?: boolean
  onDismiss?: () => void
}

/**
 * Imperative controller — call wanderingModal.show() / .hide() directly
 * instead of relying on React prop updates (which React 18 can batch/skip).
 */
export const wanderingModal = {
  show: (() => {}) as () => void,
  hide: (() => {}) as () => void,
}

function WaveformBar({ index }: { index: number }) {
  return (
    <div
      className="w-[3px] rounded-full"
      style={{
        animation: `waveform ${1.2 + index * 0.15}s ease-in-out infinite alternate`,
        animationDelay: `${index * 0.1}s`,
        background: `linear-gradient(to top, #ff006e, #00f5d4)`,
      }}
    />
  )
}

export function WanderingModal({
  allowDismiss = false,
  onDismiss,
}: WanderingModalProps) {
  const [messageIndex, setMessageIndex] = useState(0)
  const overlayRef = useRef<HTMLDivElement>(null)
  const isVisibleRef = useRef(false)
  const messageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    wanderingModal.show = () => {
      const el = overlayRef.current
      if (!el) return
      isVisibleRef.current = true
      el.style.display = "flex"
      // Give the browser one frame to register display:flex before fading in
      setTimeout(() => {
        if (overlayRef.current && isVisibleRef.current) {
          overlayRef.current.style.opacity = "1"
        }
      }, 16)
      // Start message rotation
      if (!messageIntervalRef.current) {
        messageIntervalRef.current = setInterval(() => {
          setMessageIndex((prev) => (prev + 1) % MESSAGES.length)
        }, 3500)
      }
    }

    wanderingModal.hide = () => {
      const el = overlayRef.current
      if (!el) return
      isVisibleRef.current = false
      el.style.opacity = "0"
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current)
        messageIntervalRef.current = null
      }
      setTimeout(() => {
        if (overlayRef.current && !isVisibleRef.current) {
          overlayRef.current.style.display = "none"
        }
      }, 500)
    }

    return () => {
      wanderingModal.show = () => {}
      wanderingModal.hide = () => {}
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current)
        messageIntervalRef.current = null
      }
    }
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && allowDismiss && isVisibleRef.current) {
        wanderingModal.hide()
        onDismiss?.()
      }
    },
    [allowDismiss, onDismiss]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Loading music recommendations"
      tabIndex={-1}
      className="fixed inset-0 z-50 items-center justify-center outline-none"
      style={{
        display: "none",
        opacity: 0,
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(5, 2, 10, 0.92)",
          backdropFilter: "blur(12px)",
        }}
      />

      {/* Animated nebula blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full"
          style={{
            width: "600px",
            height: "600px",
            top: "-10%",
            left: "-10%",
            background:
              "radial-gradient(circle, rgba(255,0,110,0.12) 0%, transparent 70%)",
            animation: "nebula-drift-1 12s ease-in-out infinite alternate",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "500px",
            height: "500px",
            bottom: "-15%",
            right: "-10%",
            background:
              "radial-gradient(circle, rgba(0,245,212,0.10) 0%, transparent 70%)",
            animation: "nebula-drift-2 15s ease-in-out infinite alternate",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: "400px",
            height: "400px",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background:
              "radial-gradient(circle, rgba(251,191,36,0.08) 0%, transparent 70%)",
            animation: "nebula-drift-3 18s ease-in-out infinite alternate",
          }}
        />
      </div>

      {/* Content */}
      <div
        className="relative z-10 flex flex-col items-center gap-8 px-6 py-10 max-w-lg w-full mx-4"
      >
        {/* Headline */}
        <div className="text-center">
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight"
            style={{
              color: "#faf5ff",
              textShadow: "0 0 40px rgba(255,0,110,0.3), 0 0 80px rgba(0,245,212,0.15)",
            }}
          >
            We're not searching.
          </h2>
          <p
            className="mt-3 text-2xl md:text-3xl font-light tracking-wide"
            style={{
              background: "linear-gradient(135deg, #ff006e, #00f5d4, #fbbf24)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundSize: "200% 200%",
              animation: "gradient-shift 6s ease-in-out infinite",
            }}
          >
            We're wandering.
          </p>
        </div>

        {/* ASCII Crate Panel */}
        <div
          className="relative rounded-lg px-6 py-4"
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.06)",
            animation: "ascii-breathe 4s ease-in-out infinite",
          }}
        >
          <pre
            className="font-mono text-xs md:text-sm leading-tight text-center select-none"
            style={{
              color: "rgba(0,245,212,0.5)",
              textShadow: "0 0 10px rgba(0,245,212,0.2)",
            }}
            aria-hidden="true"
          >
            {ASCII_ART}
          </pre>
        </div>

        {/* Rotating Message */}
        <div
          className="h-8 flex items-center justify-center"
          aria-live="polite"
          aria-atomic="true"
        >
          <p
            key={messageIndex}
            className="font-mono text-sm md:text-base text-center"
            style={{
              color: "rgba(250,245,255,0.7)",
              animation: "message-fade 3.5s ease-in-out",
            }}
          >
            {MESSAGES[messageIndex]}
            <span style={{ animation: "blink 1s step-end infinite" }}>
              _
            </span>
          </p>
        </div>

        {/* Waveform */}
        <div className="flex items-end gap-[3px] h-8" aria-hidden="true">
          {Array.from({ length: 16 }).map((_, i) => (
            <WaveformBar key={i} index={i} />
          ))}
        </div>

        {/* Microcopy */}
        <p
          className="text-xs font-mono tracking-wider text-center"
          style={{ color: "rgba(250,245,255,0.3)" }}
        >
          Good things take time. We're looking past the obvious.
        </p>
      </div>

      <style>{`
        @keyframes nebula-drift-1 {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(80px, 40px) scale(1.2); }
        }
        @keyframes nebula-drift-2 {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(-60px, -30px) scale(1.15); }
        }
        @keyframes nebula-drift-3 {
          0% { transform: translate(-50%, -50%) scale(1); }
          100% { transform: translate(-40%, -60%) scale(1.3); }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes waveform {
          0% { height: 4px; opacity: 0.4; }
          100% { height: 28px; opacity: 1; }
        }
        @keyframes message-fade {
          0% { opacity: 0; transform: translateY(6px); }
          12% { opacity: 1; transform: translateY(0); }
          88% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-6px); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes ascii-breathe {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  )
}
